import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../api/client';
import { actionBtnSx } from '../styles/uiTokens';
import { getAdminRole, getAdminStoreId } from '../utils/auth';

const NoticesPage = () => {
  // role/storeId 는 JWT 에서 (localStorage 변조 방지)
  const role = getAdminRole();
  const isMaster = role === 'master';
  const myStoreId = getAdminStoreId() || '';

  const [notices, setNotices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);

  // 매장 리스트 (master만 의미 있음)
  const [stores, setStores] = useState([]);

  // 생성/수정 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  // 빈 문자열 = 글로벌(전체 공통), 특정 ID = 매장 전용
  const [formStoreId, setFormStoreId] = useState('');

  useEffect(() => {
    fetchNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  // 매장 리스트 (작성 모달 매장 선택 + 목록 매장명 표시용)
  useEffect(() => {
    const url = isMaster ? '/stores/admin' : '/stores/admin/my';
    api
      .get(url)
      .then((res) => {
        // master는 array, manager는 단일 객체 또는 array 한 건
        const list = Array.isArray(res.data) ? res.data : [res.data].filter(Boolean);
        setStores(list);
      })
      .catch(() => setStores([]));
  }, [isMaster]);

  const storeName = (sid) => {
    if (!sid) return '전체 공통';
    const s = stores.find((x) => x._id === sid);
    return s?.name ?? '매장 정보 없음';
  };

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/notices', {
        params: { page, limit, search: search || undefined },
      });
      setNotices(res.data.notices || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('공지 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingNotice(null);
    setFormTitle('');
    setFormContent('');
    // master는 기본 "전체 공통", manager는 본인 매장 강제
    setFormStoreId(isMaster ? '' : myStoreId);
    setModalOpen(true);
  };

  const handleEdit = async (notice) => {
    setEditingNotice(notice);
    setFormTitle(notice.title);
    // content에 <br>이 있으면 줄바꿈으로 변환
    setFormContent((notice.content || '').replace(/<br\s*\/?>/g, '\n'));
    setFormStoreId(notice.storeId || '');
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formTitle.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    const contentWithBr = formContent.replace(/\n/g, '<br>');
    // 매니저는 백엔드가 storeId를 자기 매장으로 강제하지만, UI에서도 동일하게 묶어 보냄
    const payload = {
      title: formTitle,
      content: contentWithBr,
      storeId: formStoreId || null,
    };

    try {
      if (editingNotice) {
        await api.put(`/admin/notices/${editingNotice._id}`, payload);
        alert('수정 완료');
      } else {
        await api.post('/admin/notices', payload);
        alert('등록 완료');
      }
      setModalOpen(false);
      fetchNotices();
    } catch (err) {
      const msg = err?.response?.data?.message;
      alert(msg || (editingNotice ? '수정 실패' : '등록 실패'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말로 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/admin/notices/${id}`);
      fetchNotices();
    } catch (err) {
      alert('삭제 실패');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.slice(0, 10);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        공지사항 전체 목록
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box
          component="form"
          onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); setPage(1); }}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            size="small"
            placeholder="제목으로 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            } }}
            sx={{ width: { xs: '100%', sm: 280 } }}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 100, display: { xs: 'none', md: 'inline-flex' } }}>
          <InputLabel>표시</InputLabel>
          <Select
            value={limit}
            label="표시"
            onChange={(e) => { setLimit(e.target.value); setPage(1); }}
          >
            <MenuItem value={10}>10개</MenuItem>
            <MenuItem value={20}>20개</MenuItem>
            <MenuItem value={50}>50개</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Table sx={{ '& td[align="center"], & th[align="center"]': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 700 }}>글 제목</TableCell>
              <TableCell width="20%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>대상</TableCell>
              <TableCell width="15%" align="center" sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>생성날짜</TableCell>
              <TableCell width="20%" align="center" sx={{ fontWeight: 700 }}>관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : notices.length > 0 ? (
              notices.map((notice) => {
                const isGlobal = !notice.storeId;
                // 매니저는 글로벌 공지를 수정/삭제 못 함 (백엔드 검증과 동일)
                const canModify = isMaster || (!isGlobal && String(notice.storeId) === myStoreId);
                return (
                  <TableRow key={notice._id} hover>
                    <TableCell sx={{ fontWeight: 500, cursor: 'pointer' }}>
                      {notice.title}
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex', alignItems: 'center', minHeight: 24, px: 1, py: 0, borderRadius: '999px', fontSize: 12, fontWeight: 700,
                          backgroundColor: isGlobal ? '#e3f2fd' : '#fff3e0',
                          color: isGlobal ? '#1976d2' : '#ef6c00',
                        }}
                      >
                        {isGlobal ? '전체 공통' : storeName(notice.storeId)}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#555', display: { xs: 'none', sm: 'table-cell' } }}>
                      {formatDate(notice.createdAt)}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          onClick={() => handleEdit(notice)}
                          sx={actionBtnSx}
                          disabled={!canModify}
                        >
                          수정
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleDelete(notice._id)}
                          sx={actionBtnSx}
                          disabled={!canModify}
                        >
                          삭제
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#888' }}>
                  등록된 공지가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 + 추가 버튼 한 줄 — 모바일에선 페이지네이션 작게 (siblingCount 0) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {totalPages > 1 ? (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
          />
        ) : <Box />}
        <Button variant="contained" onClick={handleCreate} sx={actionBtnSx}>
          추가
        </Button>
      </Box>

      {/* 생성/수정 모달 */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingNotice ? '공지사항 수정' : '공지사항 등록'}</DialogTitle>
        <DialogContent>
          {/* 대상 매장 선택 */}
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="store-target-label">대상</InputLabel>
            <Select
              labelId="store-target-label"
              label="대상"
              value={formStoreId}
              onChange={(e) => setFormStoreId(e.target.value)}
              disabled={!isMaster}
            >
              {isMaster && (
                <MenuItem value="">
                  <Box component="span" sx={{ color: '#1976d2', fontWeight: 700 }}>
                    전체 공통 (모든 매장에 표시)
                  </Box>
                </MenuItem>
              )}
              {stores.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
            {!isMaster && (
              <Box sx={{ fontSize: 11, color: '#888', mt: 0.5, ml: 0.5 }}>
                매니저는 본인 매장 공지만 작성할 수 있습니다.
              </Box>
            )}
          </FormControl>
          <TextField
            label="제목"
            fullWidth
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="내용"
            fullWidth
            multiline
            rows={8}
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingNotice ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoticesPage;
